import { Component, FC, JSX } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Flyout, FlyoutContextOutgoingProps } from "@app/shared/Flyouts";
import FlyoutContextProvider, { FlyoutContext } from "@app/shared/Flyouts/FlyoutContext";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { CountyProjectContextProps } from "@app/projects/CountyProjectContext";
import AccuracyService from "@app/projects/network-assessment/accuracy/accuracyService";
import { AccuracyProviderDetail } from "@app/projects/results/accuracy/accuracyProviderDetail";
import { AccuracyProviderProfile } from "@app/projects/servicing-provider/accuracy/AccuracyProviderProfile";
import { parseQuerystring } from "@app/shared/utility/parseQuerystring";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

interface AccuracyProviderProfileFromQueryParams {
  networkType: ProviderNetworkTypeEnum;
  providerId: number;
  regionSetSpecialtyGroupId: number;
}

interface AccuracyProviderProfileFromQueryState {
  loading: boolean;
  networkType: ProviderNetworkTypeEnum;
  provider: AccuracyProviderDetail;
  providerId: number;
  regionSetSpecialtyGroupId: number;
}

type AccuracyProviderProfileFromQueryProps = RouteComponentProps<unknown> & CountyProjectContextProps & FlyoutContextOutgoingProps;

class WrappedAccuracyProviderProfileFromQuery extends Component<AccuracyProviderProfileFromQueryProps, AccuracyProviderProfileFromQueryState> {
  constructor(props: AccuracyProviderProfileFromQueryProps) {
    super(props);

    this.state = {
      loading: false,
      networkType: undefined,
      provider: undefined,
      providerId: undefined,
      regionSetSpecialtyGroupId: undefined,
    };
  }

  public componentDidMount(): void {
    this.evaluateParams();
  }

  public async componentDidUpdate(prevProps: AccuracyProviderProfileFromQueryProps): Promise<void> {
    if (prevProps.project.projectId !== this.props.project.projectId || prevProps.runConfiguration.projectRunConfigurationId !== this.props.runConfiguration.projectRunConfigurationId || prevProps.location.search !== this.props.location.search) {
      this.evaluateParams();
      return;
    }

    if (!prevProps.isFlyoutVisible && this.props.isFlyoutVisible) {
      await this.loadProviderDetail();
    }

    if (prevProps.isFlyoutVisible && !this.props.isFlyoutVisible) {
      const params = new URLSearchParams;
      params.set("networkType", undefined);
      params.set("providerId", undefined);
      params.set("regionSetSpecialtyGroupId", undefined);

      this.props.history.push({
        ...this.props.history.location,
        search: params.toString(),
      });
    }
  }

  public render(): JSX.Element {
    return (
      <Flyout content={{ body: this.renderProfile(), title: "Provider Profile" }} />
    );
  }

  private evaluateParams = () => {
    const params = parseQuery(this.props.location.search);

    if (params) {
      this.setState({
        loading: true,
        networkType: params.networkType,
        provider: undefined,
        providerId: params.providerId,
        regionSetSpecialtyGroupId: params.regionSetSpecialtyGroupId,
      }, () => this.props.setIsFlyoutVisible(true));
    } else {
      this.setState({
        loading: false,
        networkType: undefined,
        provider: undefined,
        providerId: undefined,
        regionSetSpecialtyGroupId: undefined,
      }, () => this.props.setIsFlyoutVisible(false));
    }
  }

  private loadProviderDetail = async () => {
    const provider = await new AccuracyService().accuracyProviderDetail(
      this.props.project.projectId,
      this.props.runConfiguration.projectRunConfigurationId,
      this.state.regionSetSpecialtyGroupId,
      this.state.networkType,
      this.props.networkAssessmentAnalysisType,
      this.state.providerId,
    );

    this.setState((prevState) => ({
      ...prevState,
      loading: false,
      provider,
    }));
  }

  private renderProfile = () => {
    return this.state.loading ? (
      <div className="row">
        <div className="col-12 text-center">
          <h4>
            <QesIcon iconName={QesIcons.LoadingV1} description="Loading" className="qes-icon-spin" /> Loading provider information...
          </h4>
        </div>
      </div>
    ) : (
        <AccuracyProviderProfile provider={this.state.provider} providerNetworkType={this.state.networkType} showAttestation={this.props.project.showAttestation} runConfigurationDate={this.props.runConfiguration.dateCreated } />
    );
  }
}

function parseQuery(search: string): AccuracyProviderProfileFromQueryParams {
  const queryParams = parseQuerystring(search);
  const providerParams: AccuracyProviderProfileFromQueryParams = {
    networkType: queryParams.networkType as ProviderNetworkTypeEnum,
    providerId: parseInt(queryParams.providerId, 10),
    regionSetSpecialtyGroupId: parseInt(queryParams.regionSetSpecialtyGroupId, 10),
  };
  if (providerParams && providerParams.networkType && providerParams.providerId && providerParams.regionSetSpecialtyGroupId) {
    return providerParams;
  }

  return null;
}

const AccuracyProviderProfileFromQuery: FC<RouteComponentProps<unknown> & CountyProjectContextProps> = (props: RouteComponentProps<unknown> & CountyProjectContextProps) => (
  <FlyoutContextProvider>
    <FlyoutContext.Consumer>
      {(flyoutProps) => (
        <WrappedAccuracyProviderProfileFromQuery {...props} {...flyoutProps} />
      )}
    </FlyoutContext.Consumer>
  </FlyoutContextProvider>
);

export {
  AccuracyProviderProfileFromQuery,
};
